import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Box, Divider, Drawer, Hidden, List, Typography, makeStyles } from "@material-ui/core";
import _ from "lodash";
import {
  AcceptIcon,
  ContractIcon,
  InvoiceIcon,
  CarDocumentIcon,
  CameraIcon,
  DiaryIcon,
  TollRoadIcon,
  TrafficJamIcon, // <-- ensure this exists in your icon set
} from "src/icons/FlatIcons";
import InputIcon from "@material-ui/icons/Input";
import NavItem from "./NavItem";
import * as authActions from "src/actions/authActions";
import { useCurrentRental } from "../../../hooks/rentalHooks";

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    backgroundColor: "#f3f4f8",
    width: 256,
  },
  desktopDrawer: {
    color: "black",
    backgroundColor: "#f3f4f8",
    width: 256,
    top: 69,
    zIndex: 100,
  },
  rental: {
    color: "black",
    textAlign: "center",
    "& h5": {
      fontSize: "16px",
      marginBottom: "20px",
    },
    "& p": {
      fontSize: "12px",
    },
  },
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentRental = useCurrentRental();
  const path = window.location.pathname;

  // Availability flags — adjust field names to match your API shape
  const hasTaxInvoice = Boolean(currentRental?.rental_invoice);
  const hasPrepaidInvoice = Boolean(currentRental?.prepaid_invoice);
  const hasTollsInvoice = Boolean(currentRental?.tolls);
  const hasEvInvoice = Boolean(currentRental?.ev_charges);
  const hasInfringementInvoice = Boolean(currentRental?.infringements);

  // Children for the two invoice groups
  const rentalInvoiceChildren = [
    {
      href: "/app/tax-invoice",
      icon: InvoiceIcon,
      title: "Tax Invoice",
      locked: false,
      disabled: !hasTaxInvoice,
    },
    {
      href: "/app/prepaid-invoice",
      icon: InvoiceIcon,
      title: "Pre‑paid Invoice",
      locked: false,
      disabled: !hasPrepaidInvoice,
    },
  ];

  const ancillaryInvoiceChildren = [
    {
      href: "/app/tolls-invoice",
      icon: TollRoadIcon,
      title: "Tolls Invoice",
      locked: false,
      disabled: !hasTollsInvoice,
    },
    {
      href: "/app/ev-invoice",
      icon: TollRoadIcon, // swap for EV-specific icon if you have one
      title: "EV Invoice",
      locked: false,
      disabled: !hasEvInvoice,
    },
    {
      href: "/app/infringement-invoice",
      icon: CarDocumentIcon,
      title: "Infringement Invoice",
      locked: false,
      disabled: !hasInfringementInvoice,
    },
  ];

  // Final nav items
  const items = [
    {
      icon: DiaryIcon,
      title: `Rental No: ${_.get(currentRental, "rental_agreement_number", "")}`,
      href: "/app/rentalhistory",
      locked: true,
      disabled: false,
      items: [
        {
          href: "/app/rental-photos",
          icon: CameraIcon,
          title: "Pre Rental Photos",
          locked: false,
          disabled: false,
        },
        {
          href: "/app/rental-agreement",
          icon: ContractIcon,
          title: "Rental Agreement",
          locked: false,
          disabled: false,
        },

        // ─────────────────────────────────────────────
        // NEW GROUPS (exact requirement)
        // ─────────────────────────────────────────────
        { icon: InvoiceIcon, title: "Rental Invoices", locked: false, disabled: false, items: rentalInvoiceChildren },
        { icon: TollRoadIcon, title: "Ancillary Invoices", locked: false, disabled: false, items: ancillaryInvoiceChildren },

        {
          href: "/app/vehicle-incident-reports",
          icon: CarDocumentIcon,
          title: "Vehicle Incident Reports",
          locked: !Boolean(currentRental?.virs) || (currentRental?.virs.length ?? 0) === 0,
          disabled: !Boolean(currentRental?.virs) || (currentRental?.virs.length ?? 0) === 0
        },
        // {
        //   href: "/vehicleincidents/landing-page",
        //   icon: CarDocumentIcon,
        //   title: "Lodge an Incident",
        //   locked: false
        // },
        { href: path, icon: AcceptIcon, title: "Terms & Conditions", locked: false, disabled: false, action: "openTerms" }
      ],
    },

    // External links (handled via `action` in NavItem)
    // { href: path, icon: TrafficJamIcon, title: "Fleet Guide", locked: false, disabled: false, action: "openFleetGuide" },
    // { href: path, icon: AcceptIcon, title: "Terms & Conditions", locked: false, disabled: false, action: "openTerms" },
    ...(currentRental?.brand === "Hertz"
      ? [{ href: path, icon: AcceptIcon, title: "FAQ", locked: false, disabled: false, action: "openFAQ" }]
      : []),
  ];

  // Close the mobile drawer on route change
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  function logout() {
    dispatch(authActions.logout());
    return <Navigate to="/login" />;
  }

  const content = (
    <Box display="flex" flexDirection="column">
      <Box alignItems="center" display="flex" flexDirection="column" p={2}>
        <Typography variant="subtitle1" className={classes.rental}>
          <p style={{ fontSize: "18px" }}>
            Hello{" "}
            {`${_.get(currentRental, "first_name", "")} ${_.get(currentRental, "last_name", "")}`}
          </p>
        </Typography>
      </Box>

      <Divider />

      <Box p={2}>
        <List>
          {items.map((item) => (
            <NavItem
              href={item.href}
              key={item.title}
              title={item.title}
              icon={item.icon}
              items={item.items}
              locked={item.locked}
              disabled={item.disabled}
              action={item.action}
            />
          ))}
        </List>
      </Box>

      <Divider />

      <Box alignItems="center" display="flex" flexDirection="column" p={2}>
        <a onClick={() => logout()} style={{ cursor: "pointer", marginTop: 15 }}>
          <InputIcon />
        </a>
        <a onClick={() => logout()} style={{ cursor: "pointer" }}>
          Sign Out
        </a>
      </Box>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer anchor="left" classes={{ paper: classes.desktopDrawer }} open variant="persistent">
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

export default NavBar;